/**
 * Add Payment dialog with:
 * - pull unallocated job payments
 * - POP upload + suggested field accept/reject
 * - manual capture fallback
 */

import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  addPayment,
  getUnallocatedPayments,
  uploadInvoicePaymentPop,
} from "../services/invoiceService.js";

const emptyPayment = () => ({
  amount: "",
  date: new Date().toISOString().split("T")[0],
  reference: "",
  method: "bank_transfer",
  type: "BALANCE",
  notes: "",
  sourceType: "manual",
  sourceId: null,
  proofFiles: [],
});

const AddPaymentDialog = ({ open, onClose, invoiceId, onSaved }) => {
  const [paymentData, setPaymentData] = useState(emptyPayment());
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [accepted, setAccepted] = useState({
    amount: false,
    reference: false,
    date: false,
  });
  const [uploadingPop, setUploadingPop] = useState(false);

  useEffect(() => {
    if (!open || !invoiceId) return undefined;
    let cancelled = false;
    setPaymentData(emptyPayment());
    setSuggestions(null);
    setAccepted({ amount: false, reference: false, date: false });
    setSelectedCandidateId(null);
    setError(null);
    setLoadingCandidates(true);
    getUnallocatedPayments(invoiceId)
      .then((result) => {
        if (cancelled) return;
        if (result.success) {
          setCandidates(Array.isArray(result.data) ? result.data : []);
        } else {
          setCandidates([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingCandidates(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, invoiceId]);

  const applyCandidate = (candidate) => {
    if (!candidate) {
      setSelectedCandidateId(null);
      setPaymentData((prev) => ({
        ...prev,
        sourceType: "manual",
        sourceId: null,
      }));
      return;
    }
    setSelectedCandidateId(candidate.id);
    setPaymentData((prev) => ({
      ...prev,
      amount: String(candidate.amount ?? ""),
      date: candidate.date
        ? new Date(candidate.date).toISOString().split("T")[0]
        : prev.date,
      reference: candidate.reference || prev.reference,
      method: candidate.method || prev.method,
      type: candidate.type || prev.type,
      sourceType: candidate.sourceType,
      sourceId: candidate.sourceId,
      notes: prev.notes || `Allocated from ${candidate.label}`,
    }));
  };

  const acceptSuggestion = (field) => {
    if (!suggestions) return;
    setAccepted((prev) => ({ ...prev, [field]: true }));
    if (field === "amount" && suggestions.amount != null) {
      setPaymentData((prev) => ({
        ...prev,
        amount: String(suggestions.amount),
        sourceType: prev.sourceType === "manual" ? "pop" : prev.sourceType,
      }));
    }
    if (field === "reference" && suggestions.reference) {
      setPaymentData((prev) => ({
        ...prev,
        reference: suggestions.reference,
        sourceType: prev.sourceType === "manual" ? "pop" : prev.sourceType,
      }));
    }
    if (field === "date" && suggestions.date) {
      // Keep ISO-like values; otherwise leave for ops to edit
      const parsed = Date.parse(suggestions.date);
      setPaymentData((prev) => ({
        ...prev,
        date: Number.isNaN(parsed)
          ? prev.date
          : new Date(parsed).toISOString().split("T")[0],
        sourceType: prev.sourceType === "manual" ? "pop" : prev.sourceType,
      }));
    }
  };

  const rejectSuggestion = (field) => {
    setAccepted((prev) => ({ ...prev, [field]: false }));
  };

  const handlePopUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    try {
      setUploadingPop(true);
      setError(null);
      const result = await uploadInvoicePaymentPop(invoiceId, files);
      if (!result.success) {
        setError(result.error || "Failed to upload POP");
        return;
      }
      const payload = result.data || {};
      setSuggestions(payload.suggestions || null);
      setAccepted({ amount: false, reference: false, date: false });
      setPaymentData((prev) => ({
        ...prev,
        proofFiles: payload.files || [],
        sourceType: prev.sourceType === "manual" ? "pop" : prev.sourceType,
      }));
    } catch (err) {
      setError(err.message || "Failed to upload POP");
    } finally {
      setUploadingPop(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setProcessing(true);
      setError(null);
      const result = await addPayment(invoiceId, {
        ...paymentData,
        amount: Number(paymentData.amount),
      });
      if (!result.success) {
        setError(result.error || "Failed to add payment");
        return;
      }
      onSaved?.(result.data);
      onClose?.();
    } catch (err) {
      setError(err.message || "Failed to add payment");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add Payment</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Pull unallocated order payments
            </Typography>
            {loadingCandidates ? (
              <CircularProgress size={20} />
            ) : candidates.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No unallocated quote deposits or bank matches for this order.
              </Typography>
            ) : (
              <List dense>
                {candidates.map((candidate) => (
                  <ListItem
                    key={candidate.id}
                    secondaryAction={
                      <Button
                        size="small"
                        variant={
                          selectedCandidateId === candidate.id
                            ? "contained"
                            : "outlined"
                        }
                        onClick={() =>
                          applyCandidate(
                            selectedCandidateId === candidate.id
                              ? null
                              : candidate
                          )
                        }
                      >
                        {selectedCandidateId === candidate.id
                          ? "Selected"
                          : "Use"}
                      </Button>
                    }
                  >
                    <ListItemText
                      primary={`${candidate.label} — R${Number(
                        candidate.amount || 0
                      ).toLocaleString("en-ZA")}`}
                      secondary={candidate.reference || candidate.sourceType}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Upload POP (image or PDF)
            </Typography>
            <Button variant="outlined" component="label" disabled={uploadingPop}>
              {uploadingPop ? "Reading POP…" : "Choose POP file"}
              <input
                hidden
                type="file"
                accept="image/*,application/pdf,text/plain"
                multiple
                onChange={handlePopUpload}
              />
            </Button>
            {suggestions && (
              <Stack spacing={1} sx={{ mt: 1 }}>
                {(suggestions.warnings || []).map((warning) => (
                  <Alert key={warning} severity="info">
                    {warning}
                  </Alert>
                ))}
                <Typography variant="body2">
                  Suggested amount: {suggestions.amount ?? "—"}{" "}
                  <Button size="small" onClick={() => acceptSuggestion("amount")}>
                    Use
                  </Button>
                  <Button size="small" onClick={() => rejectSuggestion("amount")}>
                    Ignore
                  </Button>
                  {accepted.amount && " ✓"}
                </Typography>
                <Typography variant="body2">
                  Suggested reference: {suggestions.reference ?? "—"}{" "}
                  <Button
                    size="small"
                    onClick={() => acceptSuggestion("reference")}
                  >
                    Use
                  </Button>
                  <Button
                    size="small"
                    onClick={() => rejectSuggestion("reference")}
                  >
                    Ignore
                  </Button>
                  {accepted.reference && " ✓"}
                </Typography>
                <Typography variant="body2">
                  Suggested date: {suggestions.date ?? "—"}{" "}
                  <Button size="small" onClick={() => acceptSuggestion("date")}>
                    Use
                  </Button>
                  <Button size="small" onClick={() => rejectSuggestion("date")}>
                    Ignore
                  </Button>
                  {accepted.date && " ✓"}
                </Typography>
              </Stack>
            )}
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Amount *"
                type="number"
                value={paymentData.amount}
                onChange={(e) =>
                  setPaymentData({ ...paymentData, amount: e.target.value })
                }
                required
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Date *"
                type="date"
                value={paymentData.date}
                onChange={(e) =>
                  setPaymentData({ ...paymentData, date: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Payment Type *"
                value={paymentData.type}
                onChange={(e) =>
                  setPaymentData({ ...paymentData, type: e.target.value })
                }
                required
              >
                <MenuItem value="DEPOSIT">Deposit</MenuItem>
                <MenuItem value="BALANCE">Balance</MenuItem>
                <MenuItem value="ADJUSTMENT">Adjustment</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reference"
                value={paymentData.reference}
                onChange={(e) =>
                  setPaymentData({ ...paymentData, reference: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Payment Method"
                value={paymentData.method}
                onChange={(e) =>
                  setPaymentData({ ...paymentData, method: e.target.value })
                }
              >
                <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="card">Card</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={paymentData.notes}
                onChange={(e) =>
                  setPaymentData({ ...paymentData, notes: e.target.value })
                }
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox checked={Boolean(selectedCandidateId)} disabled />}
                label={`Source: ${paymentData.sourceType}${
                  paymentData.sourceId ? ` (${paymentData.sourceId})` : ""
                }`}
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={processing || !paymentData.amount || !paymentData.type}
        >
          {processing ? <CircularProgress size={20} /> : "Add Payment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPaymentDialog;
